import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Check, Zap, Gift } from 'lucide-react';

const PricingSection = () => {
  const features = [
    'Complete 7-chapter guide (PDF + web)',
    'The SpendLess Stack framework',
    'Printable audit checklists',
    'Weekly spending planner',
    'Plant starter list for your climate',
    'DIY project cheat sheets',
    '90-day implementation plan'
  ];

  const bonuses = [
    { name: 'Printable Audit Checklists', value: '$19' },
    { name: 'Weekly Spending Planner', value: '$12' },
    { name: 'Plant Starter List (Climate-Specific)', value: '$15' },
    { name: 'DIY Project Cheat Sheets', value: '$24' },
    { name: '90-Day Implementation Plan', value: '$29' }
  ];

  return (
    <section id="pricing" className="py-20 px-6">
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="text-4xl md:text-5xl font-bold text-[#2C1810] mb-6 leading-tight">
          Ready to design a life that costs less and delivers more?
        </h2>
        
        <p className="text-[#5A5046] text-lg mb-16">
          SpendLess gives you the complete system — 7 chapters, practical strategies, printable tools, and a week-by-week implementation plan.
        </p>
        
        {/* Launch Special Banner */}
        <div className="bg-gradient-to-r from-[#C17855] to-[#B06745] text-white p-4 rounded-lg mb-6 shadow-lg">
          <div className="flex items-center justify-center gap-3">
            <Zap className="w-6 h-6 animate-pulse" />
            <p className="font-bold text-lg">
              Launch Special: First 100 Buyers Get 33% Off!
            </p>
            <Zap className="w-6 h-6 animate-pulse" />
          </div>
        </div>
        
        <Card className="max-w-2xl mx-auto bg-white border-t-4 border-t-[#C17855] shadow-xl p-10">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <img 
              src="/images/spendless-logo.png" 
              alt="SpendLess" 
              className="w-20 h-20 rounded-full shadow-lg"
              data-testid="pricing-logo-img"
            />
          </div>

          <div className="mb-6">
            <div className="flex items-center justify-center gap-4 mb-2">
              <span className="text-3xl text-[#5A5046] line-through">$14.99</span>
              <div className="bg-[#C17855] text-white px-4 py-1 rounded-full text-sm font-bold">
                SAVE 33%
              </div>
            </div>
            <div className="text-6xl font-bold text-[#C17855] mb-2">$9.99</div>
            <p className="text-[#5A5046] text-sm">
              Launch price for first 100 buyers only
            </p>
            <p className="text-[#5A5046] font-semibold mt-2">
              One-time payment. Lifetime access.
            </p>
          </div>
          
          <ul className="space-y-4 mb-8 text-left">
            {features.map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-1">
                  <div className="w-6 h-6 rounded-full bg-[#6B7A5C] flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                  </div>
                </div>
                <span className="text-[#2C1810]">{feature}</span>
              </li>
            ))}
          </ul>

          {/* Bonuses Section */}
          <div className="bg-[#6B7A5C] bg-opacity-10 border-2 border-[#6B7A5C] border-dashed rounded-lg p-6 mb-8">
            <div className="flex items-center justify-center gap-2 mb-4">
              <Gift className="w-6 h-6 text-[#6B7A5C]" />
              <h3 className="text-xl font-bold text-[#2C1810]">
                + FREE Bonuses Worth $99
              </h3>
            </div>
            <div className="space-y-2 text-sm">
              {bonuses.map((bonus, index) => (
                <div key={index} className="flex justify-between items-center">
                  <span className="text-[#2C1810] font-medium flex items-center gap-2">
                    <Check className="w-4 h-4 text-[#6B7A5C]" />
                    {bonus.name}
                  </span>
                  <span className="text-[#6B7A5C] font-bold">{bonus.value}</span>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-4 border-t border-[#6B7A5C] border-dashed">
              <div className="flex justify-between items-center">
                <span className="font-bold text-[#2C1810]">Total Bonus Value:</span>
                <span className="font-bold text-[#C17855] text-lg">$99</span>
              </div>
            </div>
          </div>
          
          <Link to="/products/guide">
            <Button 
              className="w-full bg-[#C17855] hover:bg-[#B06745] text-white px-8 py-6 text-lg rounded-lg transition-all shadow-md hover:shadow-lg mb-4"
              data-testid="pricing-cta-btn"
            >
              Get SpendLess + All Bonuses — $9.99
            </Button>
          </Link>
          
          <p className="text-[#5A5046] text-xs mb-2">
            Regular price $14.99 after first 100 sales
          </p>
          <p className="text-[#5A5046] text-sm">
            This guide pays for itself in the first week.
          </p>
        </Card>
      </div>
    </section>
  );
};

export default PricingSection;
